import type { MatDialog } from '@angular/material/dialog';
import type { AbstractControl } from '@angular/forms';
import type { Observable } from 'rxjs';
import { map, of, switchMap } from 'rxjs';
import type {
  AddressValidationDialogAddress,
  AddressValidationSuggestionsDialogData,
} from '@comparit/core';
import { AddressValidationSuggestionsDialogComponent } from '@comparit/core';
import type { AddressService, AddressValues } from '../services/address.service';

/**
 * The address controls of a step. `strasse` / `hausnummer` are optional because kfz's funnel
 * only asks for PLZ and Ort, while the Sach Sparten ask for the full address.
 */
export interface AddressCtrls {
  plz: AbstractControl<string | null>;
  ort: AbstractControl<string | null>;
  strasse?: AbstractControl<string | null>;
  hausnummer?: AbstractControl<string | null>;
}

export function isForwardNavigation(currentUrl: string, nextUrl: string): boolean {
  const current = extractStepNumber(currentUrl);
  const next = extractStepNumber(nextUrl);
  if (current === null || next === null) return false;
  return next > current;
}

function extractStepNumber(url: string): number | null {
  const match = url.match(/step(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Checks PLZ and Ort against the plz-ort list. For funnels that do not collect a street.
 */
export function validatePlzOrt(
  ctrls: AddressCtrls,
  addressService: AddressService,
  dialog: MatDialog
): Observable<boolean> {
  const plz = raw(ctrls.plz);
  const ort = raw(ctrls.ort);

  if (!plz || !ort) return of(true);

  if (addressService.isAddressConfirmed(addressKey({ plz, ort }))) return of(true);

  return addressService.getCitiesByZip(plz).pipe(
    switchMap(cities => {
      if (cities === null) return of(true); // API error — snackbar already shown, let user through

      const isValid = cities.some(c => c.plz === plz && c.ort.toLowerCase() === ort.toLowerCase());
      if (isValid) {
        addressService.markAddressConfirmed(addressKey({ plz, ort }));
        return of(true);
      }

      return confirmWithDialog(
        ctrls,
        { plz, ort },
        cities.map(c => ({ plz: c.plz, ort: c.ort })),
        dialog,
        addressService
      );
    })
  );
}

/**
 * Asks the backend to validate the complete address, applying its normalized spelling when it
 * recognizes it and offering suggestions when it does not.
 */
export function validateFullAddress(
  ctrls: AddressCtrls,
  addressService: AddressService,
  dialog: MatDialog
): Observable<boolean> {
  const entered = readAddress(ctrls);

  // The disabled Weiter button is not the only way forward — the browser's Forward button and a
  // typed URL both reach this guard, and canDeactivate runs before the step-access guard turns
  // them back. There is nothing to validate without all four parts, and the endpoint requires
  // them, so an incomplete address passes here and is stopped by the step-access guard instead.
  if (!entered.plz || !entered.ort || !entered.strasse || !entered.hausnummer) return of(true);

  // Already settled once — don't re-POST it, and don't re-open a dialog just dismissed.
  if (addressService.isAddressConfirmed(addressKey(entered))) return of(true);

  return addressService.validateAddress(entered).pipe(
    switchMap(result => {
      if (result === null) return of(true); // API error — snackbar already shown, let user through

      if (result.found) {
        if (result.normalized) applyAddress(ctrls, result.normalized);
        addressService.markAddressConfirmed(addressKey(readAddress(ctrls)));
        return of(true);
      }

      // A suggestion missing any part would render as a half address and apply as a no-op.
      const usable = result.suggestions.filter(s => s.plz && s.ort && s.strasse && s.hausnummer);

      return confirmWithDialog(ctrls, entered, usable, dialog, addressService);
    })
  );
}

function confirmWithDialog(
  ctrls: AddressCtrls,
  entered: AddressValues,
  suggestions: AddressValues[],
  dialog: MatDialog,
  addressService: AddressService
): Observable<boolean> {
  return dialog
    .open(AddressValidationSuggestionsDialogComponent, {
      data: {
        enteredAddress: toDialogAddress(entered),
        suggestions: suggestions.map(toDialogAddress),
      } satisfies AddressValidationSuggestionsDialogData,
    })
    .afterClosed()
    .pipe(
      map(result => {
        if (!result || result.action === 'edit') return false;
        if (result.action === 'applySuggestion') {
          applyAddress(ctrls, fromDialogAddress(result.address));
        }
        addressService.markAddressConfirmed(addressKey(readAddress(ctrls)));
        return true;
      })
    );
}

function raw(control: AbstractControl<string | null> | undefined): string {
  return control?.getRawValue()?.trim() ?? '';
}

function readAddress(ctrls: AddressCtrls): AddressValues {
  return {
    plz: raw(ctrls.plz),
    ort: raw(ctrls.ort),
    strasse: raw(ctrls.strasse),
    hausnummer: raw(ctrls.hausnummer),
  };
}

/**
 * The children are written with `emitEvent: false` so the PLZ/Straße autocompletes listening on
 * them do not re-fire. Angular propagates that flag to the ancestors too, though, and the step's
 * form group is exactly what sessionStorage and the Berechnung stream subscribe to — so the
 * group is notified once at the end, or the corrected address would never be persisted.
 */
function applyAddress(ctrls: AddressCtrls, address: AddressValues): void {
  const written = [
    setIfPresent(ctrls.plz, address.plz),
    setIfPresent(ctrls.ort, address.ort),
    setIfPresent(ctrls.strasse, address.strasse),
    setIfPresent(ctrls.hausnummer, address.hausnummer),
  ].some(Boolean);

  if (written) ctrls.plz.parent?.updateValueAndValidity();
}

function setIfPresent(
  control: AbstractControl<string | null> | undefined,
  value?: string
): boolean {
  if (!control || !value) return false;
  if (control.getRawValue() === value) return false;
  control.setValue(value, { emitEvent: false });
  return true;
}

/** Identity of an address for the confirmed-set; spelling differences are not significant. */
function addressKey(address: AddressValues): string {
  return [address.plz, address.ort, address.strasse, address.hausnummer]
    .map(part => (part ?? '').trim().toLowerCase())
    .join('|');
}

function toDialogAddress(address: AddressValues): AddressValidationDialogAddress {
  return {
    zip: address.plz,
    city: address.ort,
    street: address.strasse || undefined,
    number: address.hausnummer || undefined,
  };
}

function fromDialogAddress(address: AddressValidationDialogAddress): AddressValues {
  return {
    plz: address.zip,
    ort: address.city,
    strasse: address.street,
    hausnummer: address.number,
  };
}
